import { Button, Flex, HStack, Icon, Text, Tooltip } from '@chakra-ui/react';
import type { ElementType } from 'react';
import { TbGripVertical } from 'react-icons/tb';
import useDraggableFloatingBar from '../../../hooks/useDraggableFloatingBar';
import { PARTBAY_BLUE } from './constants';

export interface SettingsActionBarProps {
    isDirty: boolean;
    onSave: () => void;
    onDiscard: () => void;
    isSaving?: boolean;
    /** Overrides the default "Unsaved changes" copy. */
    message?: string;
    /**
     * Distance from the bottom of the positioning parent. Raise it when another
     * floating pill (e.g. the bulk-actions bar) already occupies that spot.
     */
    bottom?: number | string;
}

/**
 * Floating save/discard pill, shown while a section has unsaved changes.
 * Same shape as the bulk-actions pill used on the list pages, so the two read
 * as one pattern. The shadow here is intentional: the bar floats above content.
 */
const SettingsActionBar = ({
    isDirty,
    onSave,
    onDiscard,
    isSaving = false,
    message,
    bottom = 4,
}: SettingsActionBarProps) => {
    const { dragHandleProps } = useDraggableFloatingBar(isDirty);

    if (!isDirty) return null;

    const barButtonProps = {
        size: 'sm' as const,
        h: '32px',
        px: 4,
        borderRadius: 'full',
        fontWeight: 'medium' as const,
        fontSize: 'sm',
    };

    return (
        <Flex
            position="absolute"
            bottom={bottom}
            left={0}
            right={0}
            justify="center"
            px={4}
            zIndex={10}
            pointerEvents="none"
        >
            <HStack
                {...dragHandleProps}
                pointerEvents="auto"
                bg={PARTBAY_BLUE}
                color="white"
                borderRadius="full"
                pl={3}
                pr={2}
                py={1.5}
                spacing={3}
                boxShadow="0 8px 24px rgba(64, 81, 181, 0.35)"
                minH="44px"
                w="fit-content"
                maxW="min(100%, 800px)"
            >
                <Tooltip label="Drag to move" hasArrow placement="top" openDelay={500}>
                    <Icon
                        as={TbGripVertical as ElementType}
                        boxSize={4}
                        color="whiteAlpha.700"
                        flexShrink={0}
                        aria-hidden
                    />
                </Tooltip>

                <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
                    {message ?? 'Unsaved changes'}
                </Text>
                <HStack spacing={2}>
                    <Button
                        {...barButtonProps}
                        variant="outline"
                        borderColor="whiteAlpha.500"
                        color="white"
                        bg="whiteAlpha.200"
                        _hover={{ bg: 'whiteAlpha.300' }}
                        _active={{ bg: 'whiteAlpha.400' }}
                        isDisabled={isSaving}
                        onClick={onDiscard}
                    >
                        Discard
                    </Button>
                    <Button
                        {...barButtonProps}
                        bg="white"
                        color={PARTBAY_BLUE}
                        _hover={{ bg: 'whiteAlpha.900' }}
                        _active={{ bg: 'whiteAlpha.800' }}
                        isLoading={isSaving}
                        loadingText="Saving"
                        onClick={onSave}
                    >
                        Save changes
                    </Button>
                </HStack>
            </HStack>
        </Flex>
    );
};

export default SettingsActionBar;
